import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Container, Form, Button } from 'react-bootstrap';
import Bar from '../components/Bar';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { isAdmin } from '../utils/isAdmin';

interface ToolsNumbers {
  smicDateLabel: string;
  smicHoraire: string;
  smicMensuel: string;
  plafondAnneeLabel: string;
  plafondAnnuel: string;
  indiceCoutPeriodeLabel: string;
  indiceDuCout: string;
  indiceReferencePeriodeLabel: string;
  indiceDeReference: string;
}

const defaultNumbers: ToolsNumbers = {
  smicDateLabel: '1er juin 2026',
  smicHoraire: '12,31 € bruts',
  smicMensuel: '1867,02 € bruts',
  plafondAnneeLabel: '2026',
  plafondAnnuel: '48 060 €',
  indiceCoutPeriodeLabel: '3ème trimestre 2021',
  indiceDuCout: '1 886 €',
  indiceReferencePeriodeLabel: '3ème trimestre 2021',
  indiceDeReference: '119,70',
};

function Tools() {
  const { currentUser } = useAuth();
  const [numbers, setNumbers] = useState<ToolsNumbers>(defaultNumbers);
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState('');
  const simulateurEmbaucheRef = useRef<HTMLDivElement>(null);

  const smicDateLabelRef = useRef<HTMLInputElement>(null);
  const smicHoraireRef = useRef<HTMLInputElement>(null);
  const smicMensuelRef = useRef<HTMLInputElement>(null);
  const plafondAnneeLabelRef = useRef<HTMLInputElement>(null);
  const plafondAnnuelRef = useRef<HTMLInputElement>(null);
  const indiceCoutPeriodeLabelRef = useRef<HTMLInputElement>(null);
  const indiceDuCoutRef = useRef<HTMLInputElement>(null);
  const indiceReferencePeriodeLabelRef = useRef<HTMLInputElement>(null);
  const indiceDeReferenceRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    db.ref('toolsNumbers/current').on('value', (v: any) => {
      const values = v.val();
      if (values) {
        setNumbers({ ...defaultNumbers, ...values });
      }
    });
  }, []);

  useEffect(() => {
    const container = simulateurEmbaucheRef.current;
    if (!container || container.childElementCount > 0) {
      return;
    }
    const script = document.createElement('script');
    // async doit être désactivé pour que le script urssaf puisse
    // s'appuyer sur document.currentScript à l'exécution.
    script.async = false;
    script.dataset.module = 'simulateur-EI';
    script.dataset.couleur = '#005aa1';
    script.src = 'https://mon-entreprise.urssaf.fr/simulateur-iframe-integration.js';
    container.appendChild(script);
  }, []);

  const saveNumbers = async (e: any) => {
    e.preventDefault();
    if (
      smicDateLabelRef.current &&
      smicHoraireRef.current &&
      smicMensuelRef.current &&
      plafondAnneeLabelRef.current &&
      plafondAnnuelRef.current &&
      indiceCoutPeriodeLabelRef.current &&
      indiceDuCoutRef.current &&
      indiceReferencePeriodeLabelRef.current &&
      indiceDeReferenceRef.current
    ) {
      setSaveError('');
      try {
        await db.ref('toolsNumbers/current').set({
          smicDateLabel: smicDateLabelRef.current.value,
          smicHoraire: smicHoraireRef.current.value,
          smicMensuel: smicMensuelRef.current.value,
          plafondAnneeLabel: plafondAnneeLabelRef.current.value,
          plafondAnnuel: plafondAnnuelRef.current.value,
          indiceCoutPeriodeLabel: indiceCoutPeriodeLabelRef.current.value,
          indiceDuCout: indiceDuCoutRef.current.value,
          indiceReferencePeriodeLabel: indiceReferencePeriodeLabelRef.current.value,
          indiceDeReference: indiceDeReferenceRef.current.value,
        });
        setEditing(false);
      } catch (err: any) {
        setSaveError(err && err.message ? err.message : String(err));
      }
    }
  };

  return (
    <>
      <Container className="wg-100">
        <Row className="justify-content-center text-center" style={{ color: "#1155bf", paddingBottom: "10vh" }} >
          <h1>Outils</h1>
        </Row>
        <Row className="text-dark" >

          <Col xs={ 12 } >
            <Row className="align-items-center">
              <Col>
                <h2>Chiffres clés :</h2>
              </Col>
              { isAdmin(currentUser) &&
              <Col xs="auto">
                <Button variant="outline-primary" size="sm" onClick={ () => setEditing(!editing) } >
                  { editing ? 'Annuler' : 'Éditer' }
                </Button>
              </Col>
              }
            </Row>

            { editing &&
            <Form onSubmit={ saveNumbers } className="mb-4" >
              <Form.Group controlId="smicDateLabel">
                <Form.Label>Date de mise à jour du Smic</Form.Label>
                <Form.Control ref={ smicDateLabelRef } defaultValue={ numbers.smicDateLabel } />
              </Form.Group>
              <Form.Group controlId="smicHoraire">
                <Form.Label>Smic horaire</Form.Label>
                <Form.Control ref={ smicHoraireRef } defaultValue={ numbers.smicHoraire } />
              </Form.Group>
              <Form.Group controlId="smicMensuel">
                <Form.Label>Smic mensuel (35 h)</Form.Label>
                <Form.Control ref={ smicMensuelRef } defaultValue={ numbers.smicMensuel } />
              </Form.Group>
              <Form.Group controlId="plafondAnneeLabel">
                <Form.Label>Année du plafond de Sécurité Sociale</Form.Label>
                <Form.Control ref={ plafondAnneeLabelRef } defaultValue={ numbers.plafondAnneeLabel } />
              </Form.Group>
              <Form.Group controlId="plafondAnnuel">
                <Form.Label>Plafond annuel de Sécurité Sociale</Form.Label>
                <Form.Control ref={ plafondAnnuelRef } defaultValue={ numbers.plafondAnnuel } />
              </Form.Group>
              <Form.Group controlId="indiceCoutPeriodeLabel">
                <Form.Label>Période de l'indice du coût de la construction</Form.Label>
                <Form.Control ref={ indiceCoutPeriodeLabelRef } defaultValue={ numbers.indiceCoutPeriodeLabel } />
              </Form.Group>
              <Form.Group controlId="indiceDuCout">
                <Form.Label>Indice du coût de la construction</Form.Label>
                <Form.Control ref={ indiceDuCoutRef } defaultValue={ numbers.indiceDuCout } />
              </Form.Group>
              <Form.Group controlId="indiceReferencePeriodeLabel">
                <Form.Label>Période de l'indice de référence des loyers commerciaux</Form.Label>
                <Form.Control ref={ indiceReferencePeriodeLabelRef } defaultValue={ numbers.indiceReferencePeriodeLabel } />
              </Form.Group>
              <Form.Group controlId="indiceDeReference">
                <Form.Label>Indice de référence des loyers commerciaux</Form.Label>
                <Form.Control ref={ indiceDeReferenceRef } defaultValue={ numbers.indiceDeReference } />
              </Form.Group>
              { saveError &&
              <p className="text-danger">Erreur lors de l'enregistrement : { saveError }</p>
              }
              <Button variant="primary" type="submit" >
                Enregistrer
              </Button>
            </Form>
            }

            <p>
              Smic horaire au { numbers.smicDateLabel } : <b>{ numbers.smicHoraire } de l'heure.</b>
              <br/>
              Smic mensuel pour 35 h au { numbers.smicDateLabel } : <b>{ numbers.smicMensuel }</b>.
              <br/>
              Plafond annuel de Sécurité Sociale { numbers.plafondAnneeLabel } : <b>{ numbers.plafondAnnuel }</b>
              <br/>
              <a href="https://www.insee.fr/fr/themes/conjoncture/indice_icc.asp">
                Indice du coût de la construction au { numbers.indiceCoutPeriodeLabel } : <b>{ numbers.indiceDuCout }</b>
              </a>
              <br/>
              <a href="https://www.insee.fr/fr/statistiques/serie/001532540">
                Indice de référence des loyers commerciaux - { numbers.indiceReferencePeriodeLabel } : <b>{ numbers.indiceDeReference }</b>
              </a>
            </p>
          </Col>

        </Row>
        <Bar />

        <Row className="justify-content-center text-dark" style={{ paddingBottom: '5vh' }} >
          <Col xs={ 12 } >
            <h2>Simulateur d'embauche</h2>
            <div ref={ simulateurEmbaucheRef } />
          </Col>
        </Row>

        <Row className="justify-content-center text-dark" style={{ paddingBottom: '5vh' }} >
          <Col xs={ 12 } >
            <h2><a href="https://www.service-public.fr/simulateur/calcul/gratification-stagiaire">
                Simulateur de calcul de la gratification minimale
                d'un stagiaire (lien externe)
            </a></h2>
          </Col>
        </Row>

        <Row className="justify-content-center text-dark" style={{ paddingBottom: '5vh' }} >
          <Col xs={ 12 } >
            <h2><a href="https://www.alternance.emploi.gouv.fr/portail_alternance/jcms/hl_6238/simulateur-alternant">
                Simulateur du cout de l'embauche d'un apprenti (lien externe)
            </a></h2>
          </Col>
        </Row>

        <Row className="justify-content-center text-dark" >
          <Col xs={ 12 } >
            <h2><a href="https://autodiagnostics.experts-comptables.org/autodiag/autodiagnostic_cybersecurite/questionnaire.htm?id_EC=e8157921-00e1-3f41-7a75-53b55753ae22&email_EC=nl@lefevre-ec.fr#2">
                Autodiagnostic Cybersecurité (lien externe)
            </a></h2>
          </Col>
        </Row>

        <Bar />

        <Row className="justify-content-center text-dark" style={{ paddingBottom: '5vh' }} >
          <Col xs={ 12 } >
            <h2>
                Fichiers utiles
            </h2>
          </Col>
        </Row>

        <Row className="text-dark" style={{ paddingBottom: '5vh' }} >

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/note-de-frais.xlsx" >
              <img src="images/image-note-de-frais.png" alt="note de frais" />
              <br/>
              <h3>Note de Frais</h3>
              <p>Ce fichier permet de calculer les remboursements de frais.</p>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="https://www.economie.gouv.fr/particuliers/bareme-kilometrique" >
              <img src="images/fotolia-circulation-910.png" alt="bareme km" />
              <br/>
              <h3>Barèmes des indemnités kilomètriques</h3>
              <p>Tout savoir sur le barème des frais kilométriques</p>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/calcul-indemnite-km.xlsx" >
              <img src="images/image-indemnite-kilometrique.png" alt="indmnite km" />
              <br/>
              <h3>Indemnité kilomètrique</h3>
              <p>Ce fichier permet de calculer en fonction des
    déplacements le coût des indemnités kilométriques.</p>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/liste-cheque-emis.xls" >
              <img src="images/image-liste-cheques-emis.png" alt="cheque emis" />
              <br/>
              <h3>Liste des chèques émis</h3>
                <p>Ce fichier permet de lister les chèques
    émis triés par numéro de chèque.</p>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/LEC Modèle Caisse.xlsx" >
              <img src="images/image-liste-cheques-emis.png" alt="caisse annuel" />
              <br/>
              <h3>LEC Modèle Caisse annuel</h3>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/LEC Modèle Caisse mensuel.xlsx" >
              <img src="images/image-liste-cheques-emis.png" alt="caisse mensuel" />
              <br/>
              <h3>LEC Modèle Caisse mensuel</h3>
            </a>
          </Col>

          <Col xs={ 6 } md={ 4 } lg={ 3 } className="text-center" >
            <a href="download/Fiche prévention risques.xlsx" >
              <img src="images/compte_prevention_penibilite_340_158.jpg" alt="compte prevention" />
              <br/>
              <h3>
                Fiche de prévention des expositions à
                certains facteurs de risques professionnels
              </h3>
              <p>
                Cette fiche recense les expositions à facteurs
                de risques pour chaque salarié.
              </p>
            </a>
          </Col>

        </Row>

      </Container>
  </>
  );
}

export default Tools;
